import { useState } from 'react'
import type { AdminUserRow, AdminUserSortBy } from '@ai-meeting/shared'
import { useUiStrings } from '@/hooks/useUiStrings'
import { fetchAdminUsers } from '@/services/admin'
import AdminDataTable, { type AdminColumn } from './AdminDataTable'
import AdminSearchInput from './AdminSearchInput'
import UserActivityDrawer from './UserActivityDrawer'
import { formatDateTime } from './adminFormat'
import { useAdminTable } from './useAdminTable'

interface UsersTabProps {
  enabled: boolean
}

/**
 * Users summary tab: paginated per-user aggregates (logins, meetings, ratings)
 * with search and column sorting.
 *
 * Each row opens that person's activity log in a drawer (BRAI-677). The button
 * is the first column rather than the last: this table is ten columns wide and
 * scrolls sideways, and an action parked past the right edge is one nobody
 * finds.
 */
export default function UsersTab({ enabled }: UsersTabProps) {
  const { t, locale } = useUiStrings()
  const [activityUser, setActivityUser] = useState<AdminUserRow | null>(null)
  const table = useAdminTable<AdminUserRow, AdminUserSortBy>({
    fetcher: fetchAdminUsers,
    defaultSortBy: 'createdAt',
    enabled,
  })

  const columns: AdminColumn<AdminUserRow, AdminUserSortBy>[] = [
    {
      key: 'activity',
      label: t.adminColActivity,
      render: (r) => (
        <button
          type="button"
          onClick={() => setActivityUser(r)}
          aria-label={`${t.adminUserActivityTitle} — ${r.email}`}
          className="rounded-lg border border-[var(--border-color)] px-2.5 py-1 text-xs font-semibold text-[var(--accent)] transition-colors hover:bg-[var(--bg-tertiary)]"
        >
          {t.adminViewActivity}
        </button>
      ),
    },
    { key: 'email', label: t.adminColEmail, sortBy: 'email', render: (r) => r.email },
    { key: 'name', label: t.adminColName, sortBy: 'name', render: (r) => r.name },
    { key: 'role', label: t.adminColRole, sortBy: 'role', render: (r) => r.role },
    {
      key: 'authProvider',
      label: t.adminColProvider,
      sortBy: 'authProvider',
      render: (r) => r.authProvider,
    },
    {
      key: 'loginCount',
      label: t.adminColLoginCount,
      sortBy: 'loginCount',
      align: 'right',
      render: (r) => r.loginCount,
    },
    {
      key: 'lastLogin',
      label: t.adminColLastLogin,
      sortBy: 'lastLogin',
      render: (r) => formatDateTime(r.lastLogin, locale),
    },
    {
      key: 'meetingCount',
      label: t.adminColMeetingCount,
      sortBy: 'meetingCount',
      align: 'right',
      render: (r) => r.meetingCount,
    },
    {
      key: 'totalMinutes',
      label: t.adminColTotalMinutes,
      sortBy: 'totalMinutes',
      align: 'right',
      render: (r) => r.totalMinutes,
    },
    {
      key: 'avgRating',
      label: t.adminColAvgRating,
      sortBy: 'avgRating',
      align: 'right',
      render: (r) => r.avgRating ?? '—',
    },
    {
      key: 'createdAt',
      label: t.adminColCreatedAt,
      sortBy: 'createdAt',
      render: (r) => formatDateTime(r.createdAt, locale),
    },
  ]

  return (
    <div className="flex flex-col gap-4">
      {activityUser && (
        <UserActivityDrawer user={activityUser} onClose={() => setActivityUser(null)} />
      )}
      <div className="flex flex-wrap items-center gap-3">
        <AdminSearchInput
          value={table.search}
          placeholder={t.adminSearchUsers}
          onChange={table.setSearch}
        />
      </div>
      <AdminDataTable
        columns={columns}
        rows={table.rows}
        rowKey={(r) => r.id}
        loading={table.loading}
        error={table.error}
        sortBy={table.sortBy}
        sortDir={table.sortDir}
        onToggleSort={table.toggleSort}
        page={table.page}
        totalPages={table.totalPages}
        total={table.total}
        onPageChange={table.setPage}
      />
    </div>
  )
}
